'use client'

import { useCallback, useEffect, useRef, useState } from 'react'

import { mockHomeContentList } from '@/lib/data/mockHomeData'
import { useHLClearingHouseState } from '@/lib/hooks/useMargin'
import { useUserFills } from '@/lib/hooks/useUserFills'
import type { UserFills } from '@/lib/utils/hyper/types'
import { useParams, useRouter, useSearchParams } from 'next/navigation'
import { useTranslations } from 'next-intl'
import { HexString } from '@/lib/config/wagmi'
import { addSymbolSuffix } from '@/lib/hooks/useHLCoinInfo'
import { PositionHistoryItem } from '@/components/trader/PositionHistoryItem'
import { Box, HStack, Text, VStack } from '@chakra-ui/react'
import { PositionDetailHeader } from '@/components/trader/PositionDetailHeader'
import { LoadingState } from '@/components/common/LoadingState'
import { PositionDetailNav } from '@/components/trader/PositionDetailNav'
import { useWindowVirtualizer } from '@tanstack/react-virtual'
import { formatPercentageDisplay } from '@/lib/utils/format'
import { useMarketTicker } from '@/lib/hooks/usePerpMarket'

export default function PositionDetailPageMobile() {
  const t = useTranslations()

  const router = useRouter()

  const params = useParams()
  const searchParams = useSearchParams()
  const address = params.address as string
  const positionId = params.positionId as string
  const from = searchParams.get('from')
  const nonce = searchParams.get('nonce')

  // Track if user actually navigated from the trader page via app navigation
  const [canUseBack, setCanUseBack] = useState(false)

  useEffect(() => {
    if (typeof window !== 'undefined' && address) {
      // Check sessionStorage for navigation marker set by the previous page
      const navigationKey = `nav_from_trader_${address}`
      const hasNavigationMarker = !!nonce && sessionStorage.getItem(navigationKey) === nonce

      setCanUseBack(hasNavigationMarker)
    }
  }, [address])

  const listRef = useRef<HTMLDivElement>(null)

  const { data: clearingHouseState, isLoading } = useHLClearingHouseState({
    user: address as HexString,
  })

  const coinSymbol = positionId?.replaceAll('-PERP', '') || ''
  // Fetch user fills for history
  const { data: userFills, isLoading: isHistoryLoading } = useUserFills(address as HexString)

  const { data: marketTicker } = useMarketTicker(coinSymbol, undefined)

  const filteredFills = userFills?.filter(
    fill => fill.coin.replaceAll('-PERP', '') === positionId.replaceAll('-PERP', ''),
  )

  const fillVirtualizer = useWindowVirtualizer({
    count: filteredFills?.length || 0,
    estimateSize: () => 50 + 6 * 4,
    overscan: 5,
    scrollMargin: listRef.current?.offsetTop ?? 0,
  })

  // Find the specific position
  const position = clearingHouseState?.assetPositions.find(
    ap => ap.position.coin === addSymbolSuffix(positionId),
  )?.position || null

  // Find user data from mock data
  const userData = mockHomeContentList.find(trader => trader.user.address === address)?.user || null

  // Calculate price display data
  let priceDisplayData: { price: number, changePercent: string, isPositive: boolean }

  if (!position) {
    priceDisplayData = { price: 0, changePercent: '- -', isPositive: true }
  } else {
    const entryPrice = Number.parseFloat(position.entryPx || '0')
    const unrealizedPnl = Number.parseFloat(position.unrealizedPnl || '0')
    const roi = Number.parseFloat(position.returnOnEquity || '0')

    // Calculate current price based on entry price and PnL
    const currentPrice = marketTicker?.mark_price ?? marketTicker?.last_price ?? 0
    const changePercent = marketTicker?.change_24h_percent ? formatPercentageDisplay(marketTicker?.change_24h_percent) : '---'
    const isPositive = marketTicker?.change_24h_percent ? marketTicker.change_24h_percent >= 0 : false

    priceDisplayData = {
      price: currentPrice,
      changePercent,
      isPositive: isPositive,
    }
  }

  // console.log('Position Detail Page', { address, positionId, position }, clearingHouseState?.assetPositions)
  const handleGoBack = useCallback(() => {
    // Only use router.back() if user actually came from the trader page
    // This prevents issues when users directly access the URL (even with ?from=trader)
    if (from === 'trader' && canUseBack) {
      router.back()
    }
    else {
      // Always navigate to the trader page as a fallback
      // This handles:
      // 1. Direct URL access (with or without ?from=trader)
      // 2. Navigation from other sources
      // 3. Bookmarks/shared links
      router.push(`/trader/${address}`)
    }
  }, [from, canUseBack, router, address])
  const handleAvatarPress = () => {
    handleGoBack()
  }

  if (isLoading) {
    return (
      <Box flex={1}>
        <LoadingState />
      </Box>
    )
  }

  if (!position) {
    return (
      <Box flex={1}>
        <HStack flex={1} align="center" justify="center" px={4}>
          <Text textAlign="center" fontSize="md" color="text.t1">{t('trader.position.notFound')}</Text>
        </HStack>
      </Box>
    )
  }

  return (
    <Box bg="bg.t0">
      <PositionDetailNav position={position} onBack={handleGoBack} />
      
      <Box flex={1} w="full" overflowY="auto">
        {filteredFills && filteredFills.length > 0 ? (
          <VStack gap={0} align="stretch">
            {/* Section Header */}
            <PositionDetailHeader
              position={position!}
              userAddress={address}
              userData={userData}
              priceDisplayData={priceDisplayData}
              filteredFills={filteredFills}
              handleAvatarPress={handleAvatarPress}
            />
            
            {/* Section Items */}
            <Box px={4} >
              <Box ref={listRef} pos="relative" height={`${fillVirtualizer.getTotalSize()}px`}>
                {fillVirtualizer.getVirtualItems().map((item) => (
                  <Box 
                    key={filteredFills[item.index].tid} 
                    pos="absolute" 
                    top={0} 
                    left={0} 
                    h={`${item.size}px`} 
                    w="full"
                    transform={`translateY(${item.start - fillVirtualizer.options.scrollMargin}px)`}
                  >
                    <PositionHistoryItem fill={filteredFills[item.index]} index={item.index} totalItems={filteredFills?.length || 0} />
                  </Box>
                ))}
              </Box>
            </Box>
          </VStack>
        ) : (
          <VStack gap={0} align="stretch">
            {/* Section Header */}
            <PositionDetailHeader
              position={position!}
              userAddress={address}
              userData={userData}
              priceDisplayData={priceDisplayData}
              filteredFills={filteredFills}
              handleAvatarPress={handleAvatarPress}
            />
            
            {/* Empty State */}
            <HStack flex={1} align="center" justify="center" py={8}>
              <Text textAlign="center" fontSize="sm" color="text.t1/60">
                {isHistoryLoading ? t('trader.history.loading') : t('trader.history.noHistory')}
              </Text>
            </HStack>
          </VStack>
        )}
      </Box>
    </Box>
  )
}
